import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import MainToggle from '@wix/da-ds/pkg/formControls/dsPresets/MainToggle';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import {
  getIsAdopted,
  getIsOnSale,
  getIsOwner,
} from '@wix/da-shared-react/pkg/utils/adoptables';
import { getSellableAs } from '@wix/da-shared-react/pkg/types/deviation/getters';
import { SellableAs } from '@wix/da-shared-react/pkg/types/deviation';
import { pushAdoptsSellerModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useCoreMonetizationUpsellPopup } from '../../modals/CoreMonetizationUpsellPopup/useCoreMonetizationUpsellPopup';
import { refresh } from '../../redux/actions/modal';
import { useSubmitField } from '../../hooks/useSubmitField';
import { SubmitFormContext } from '../../context';
import CreateExclusiveForm from './CreateExclusiveForm';
import EditExclusiveForm from './EditExclusiveForm';
import { initAdapter } from './adapters';
import ExclusiveOwnerDisplay from './ExclusiveOwnerDisplay';

import s from './SellExclusiveField.scss';

interface Props {
  className?: string;
}

const SellExclusiveField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const { initResponse } = useContext(SubmitFormContext) || {};
  const deviation = initResponse!.deviation!;

  const { showCoreUpsellPopup, coreUpsellPopup } =
    useCoreMonetizationUpsellPopup();

  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const dispatchSubmitModalRefresh = useAction(refresh);

  const {
    id,
    field: { onChange },
  } = useSubmitField('sellExclusiveToggle', {
    initAdapter,
  });

  const { sellExclusiveToggle: isToggleOn, showAttachments: isPcpToggleOn } =
    useWatch({
      name: ['sellExclusiveToggle', 'showAttachments'],
    });

  // Check if deviation can be sold as exclusive.
  // Consider edge case: if deviation is currently sold as PCP but user has disabled
  // the PCP toggle locally (without saving), allow exclusive selling since PCP will be disabled.
  const sellableAs = getSellableAs(deviation);
  const isOnSale = getIsOnSale(deviation);
  const canCreateExclusive = useMemo(
    () =>
      !isPcpToggleOn &&
      !isOnSale &&
      (sellableAs.includes(SellableAs.Adoptable) ||
        sellableAs.includes(SellableAs.AdoptableResale) ||
        deviation.onSaleAs?.includes(SellableAs.Pcp)),
    [sellableAs, isPcpToggleOn, isOnSale, deviation]
  );

  const owner = deviation.adoptable?.owner;
  const isOwner = getIsOwner(deviation);
  const isSold = getIsAdopted(deviation) || (isOnSale && !isOwner);

  const canEditExclusive = isOnSale && isOwner;
  const toggleIsDisabled = !canCreateExclusive && !canEditExclusive;

  const handleToggleChange = useCallback(() => {
    if (!hasCoreMembership) {
      showCoreUpsellPopup();
      return;
    }

    if (isOnSale) {
      dispatchPushAdoptsSellerModal([deviation], 'end-confirm', {
        resetScreensOnDismiss: false,
        doReload: false,
        onSuccess: () => {
          // when sale is ended, switch toggle off and refresh modal data
          onChange(false);
          dispatchSubmitModalRefresh({ deviationId: deviation.deviationId });
        },
      });
      return;
    }

    onChange(!isToggleOn);
  }, [
    hasCoreMembership,
    showCoreUpsellPopup,
    isToggleOn,
    isOnSale,
    onChange,
    dispatchPushAdoptsSellerModal,
    dispatchSubmitModalRefresh,
    deviation,
  ]);

  const label = (
    <span className={s['label']}>
      {t('deviationSubmit.form.sellExclusive.label')}
      <SimpleTooltip
        text={t('deviationSubmit.form.sellExclusive.tooltip', {
          postProcess: ['reactPostprocessor'],
          learnMoreLink: (
            <a
              href={t('deviationSubmit.form.sellExclusive.tooltip.url')}
              rel="license noopener noreferrer"
              target="_blank"
            >
              {t('deviationSubmit.form.sellExclusive.tooltip.link')}
            </a>
          ),
        })}
        stopPropagation={true}
      >
        <InfoIcon
          className={s['tooltip-icon']}
          size={IconSize.SMALLER}
          disableDefaultColors
        />
      </SimpleTooltip>
    </span>
  );

  const toggle = !isSold && (
    <MainToggle
      variant="main"
      id={id}
      size={'small'}
      value={isToggleOn}
      disabled={toggleIsDisabled}
      onChange={handleToggleChange}
      aria-labelledby={`${id}-label`}
    />
  );

  return (
    <div className={className}>
      <ButtonFieldLayout
        label={label}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
      >
        {toggleIsDisabled ? (
          <SimpleTooltip
            text={t('deviationSubmit.form.sellExclusive.disabled.tooltip')}
            usePortal
          >
            {toggle}
          </SimpleTooltip>
        ) : (
          toggle
        )}
      </ButtonFieldLayout>
      {isToggleOn && canCreateExclusive && (
        <CreateExclusiveForm deviation={deviation} />
      )}
      {isToggleOn && canEditExclusive && (
        <EditExclusiveForm deviation={deviation} />
      )}
      {isSold && owner && <ExclusiveOwnerDisplay owner={owner} />}

      {coreUpsellPopup}
    </div>
  );
};

SellExclusiveField.displayName = 'SellExclusiveField';

export default SellExclusiveField;
